
<template>
  <div>
    <h2
      id="customer-admin-groupsheader"
      class="sr-only"
    >
      Manage Groups Page
    </h2>
    <admin-stats
      :users="users"
    />
    <div class="add-new-group-link">
      <router-link :to="{ name: 'customer-admin-groups-groupId', params: { groupId: 0 } }">
        <button
          class="btn btn-primary"
          style="margin-top: 30px;"
        >
          ADD NEW {{ (isEdu) ? 'CLASS' : 'GROUP' }}
        </button>
      </router-link>
    </div>
    <div
      v-if="groups"
      class="table-responsive"
    >
      <table
        :key="adminGroupsKey"
        class="table table-striped table-bordered table-condensed"
      >
        <caption>List of all {{ isEdu ? 'Classes' : 'Groups' }}</caption>
        <thead>
          <tr>
            <th
              width="20%"
              scope="col"
              style="cursor: pointer;"
              @click.prevent="sortTable('adminGroups', 'groupName')"
            >
              Name
              <svg
                v-if="adminGroupsSort == 'groupName-d' || adminGroupsSort == 'groupName-u'"
                class="icon-arrow"
              >
                <use :xlink:href="(adminGroupsSort == 'groupName-d') ? '/static/svg-symbols.svg#icon-arrow-down' : '/static/svg-symbols.svg#icon-arrow-up'" />
              </svg>
            </th>
            <th
              width="5%"
              scope="col"
              style="cursor: pointer;"
              @click.prevent="sortTable('adminGroups', 'activeUsers')"
            >
              Active Users
              <svg
                v-if="adminGroupsSort == 'activeUsers-d' || adminGroupsSort == 'activeUsers-u'"
                class="icon-arrow"
              >
                <use :xlink:href="(adminGroupsSort == 'activeUsers-d') ? '/static/svg-symbols.svg#icon-arrow-down' : '/static/svg-symbols.svg#icon-arrow-up'" />
              </svg>
            </th>
            <th
              width="5%"
              scope="col"
              style="cursor: pointer;"
              @click.prevent="sortTable('adminGroups', 'totalUsers')"
            >
              Total Users
              <svg
                v-if="adminGroupsSort == 'totalUsers-d' || adminGroupsSort == 'totalUsers-u'"
                class="icon-arrow"
              >
                <use :xlink:href="(adminGroupsSort == 'totalUsers-d') ? '/static/svg-symbols.svg#icon-arrow-down' : '/static/svg-symbols.svg#icon-arrow-up'" />
              </svg>
            </th>
            <th
              width="35%"
              scope="col"
            >
              Invite Code &amp; Link
              <br><small>(For adding new speakers)</small>
            </th>
            <th
              width="8%"
              scope="col"
              style="cursor: pointer;"
              @click.prevent="sortTable('adminGroups', 'since')"
            >
              Since
              <svg
                v-if="adminGroupsSort == 'since-d' || adminGroupsSort == 'since-u'"
                class="icon-arrow"
              >
                <use :xlink:href="(adminGroupsSort == 'since-d') ? '/static/svg-symbols.svg#icon-arrow-down' : '/static/svg-symbols.svg#icon-arrow-up'" />
              </svg>
            </th>
            <th
              v-if="!org.hasPerpetualFreeDuration"
              width="10%"
              scope="col"
            >
              Free Duration for {{ isEdu ? 'Student' : 'Speaker' }} Access
            </th>
            <th
              v-if="!hidePrivacy"
              width="17%"
              scope="col"
            >
              {{ isEdu ? 'Instructor' : 'Coach' }} Privacy Level/<br>{{ isEdu ? 'Student' : 'Speaker' }} Privacy Level
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td scope="row">
              [
              <router-link
                :to="{ name: 'customer-admin-users-groupId', params: { groupId: '00000000-0000-4000-0000-000000000000' }}"
              >
                {{ isEdu ? 'Instructors' : 'Coaches' }} and Admins
              </router-link>
              ]
            </td>
            <td>
              <router-link
                :to="{ name: 'customer-admin-users-groupId', params: { groupId: '00000000-0000-4000-0000-000000000000' }, query: { active: 1} }"
              >
                {{ countUsers({groupId: '00000000-0000-4000-0000-000000000000'}, 'active') }} users
              </router-link>
            </td>
            <td>
              <router-link
                :to="{ name: 'customer-admin-users-groupId', params: { groupId: '00000000-0000-4000-0000-000000000000' }}"
              >
                {{ countUsers({groupId: '00000000-0000-4000-0000-000000000000'}) }} users
              </router-link>
            </td>
            <td>&mdash;</td>
            <td>&mdash;</td>
            <td
              v-if="!org.hasPerpetualFreeDuration"
            >
              &mdash;
            </td>
            <td v-if="!hidePrivacy">
              &mdash;
            </td>
          </tr>
          <tr v-for="group in sortGroups(groups)">
            <td scope="row">
              <router-link
                :to="{ name: 'customer-admin-users-groupId', params: { groupId: group.groupId } }"
              >
                {{ group.name }}
              </router-link>
            </td>
            <td>
              <router-link
                :to="{ name: 'customer-admin-users-groupId', params: { groupId: group.groupId }, query: { active: 1} }"
              >
                {{ countUsers(group, 'active') }} users
              </router-link>
            </td>
            <td>
              <router-link
                :to="{ name: 'customer-admin-users-groupId', params: { groupId: group.groupId } }"
              >
                {{ countUsers(group) }} users
              </router-link>
            </td>
            <td>
              <span v-if="group.inviteCode">
                <tt>{{ group.inviteCode }}</tt>
                <a
                  :href="group.inviteLink"
                >
                  (link)
                </a>
              </span>
              <i v-if="!group.inviteCode">
                (none / invite-only)
              </i>
            </td>
            <td>{{ group.createdDate | dateShort }}</td>
            <td
              v-if="!org.hasPerpetualFreeDuration"
            >
              {{ getFreeDuration(group) || '&mdash;' }}
            </td>
            <td v-if="typeof group.managerPrivacyLevel === 'number' && !hidePrivacy">
              {{ mapPrivacyLevel(group.managerPrivacyLevel) }} / {{ mapPrivacyLevel(group.speakerPrivacyLevel) }}
            </td>
            <td v-else-if="!hidePrivacy" />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .add-new-group-link {
    margin: 4px 0 15px 4px;
    text-align: right;
  }
  .icon-arrow {
    width: 16px; height: 16px;
    position: relative; top: 3px;
  }
</style>

<script>
import AdminStats from '../forms/AdminStats';
import TableMethods from '../specials/TableMethods';

export default {
  components: {AdminStats},
  mixins: [TableMethods],
  props: {
    hidePrivacy: {
      type: Boolean,
      default: false
    },
    org: {
      type: Object,
      required: true
    },
    users: {
      type: Object,
      required: true
    },
    isEdu: {
      type: Boolean,
      default: false
    },
    groups: {
      type: Array,
      required: true
    },
    groupIdsToNames: {},
  },
  data() {
    return {
      adminGroupsSort: 'groupName-d',
      adminGroupsKey: 1,
    };
  },
  created() {
    ['adminGroupsSort'].forEach((pref) => {
      if (this.preferences && this.preferences[pref]) {
        this[pref] = this.preferences[pref];
      }
    });
  },
  methods: {
    countUsers(group, type='all') {
      if (!this.users || !this.users[group.groupId]) {
        return 0;
      }
      if (type === 'all') {
        return this._.size(this.users[group.groupId]);
      }
      return this._.size(_.filter(this.users[group.groupId], {membershipStatus: 'active'}));
    },
    getFreeDuration(group) {
      if (group && group.settings && group.settings.freeDuration) {
        return this.$options.filters.momentDuration(group.settings.freeDuration);
      } else if (this.org && this.org.settings && this.org.settings.freeDuration) {
        return this.$options.filters.momentDuration(this.org.settings.freeDuration);
      }
      return false;
    },
    mapPrivacyLevel(num) {
      switch(num) {
      case 0:
        return 'Sharing Disabled';
      case 1:
        return 'Low';
      case 2:
        return 'Medium';
      case 3:
        return 'High';
      case 4:
        return 'Highest';
      }
    },
    sortGroups(groups) {
      return this.sortCollection(groups, this.adminGroupsSort);
    }
  }
};
</script>
